import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Music, Users, Lightbulb, Plane, Wand2, Cog, Target, Gift } from 'lucide-react';
import { SERVICES } from '@/constants/testIds';

const IMGS = {
    corporate: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85',
    production: 'https://images.unsplash.com/photo-1523525930602-e1239a31310b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHw0fHxjaW5lbWF0aWMlMjBjb3Jwb3JhdGUlMjBldmVudCUyMHN0YWdlJTIwbGVkJTIwd2FsbCUyMGxpZ2h0aW5nfGVufDB8fHx8MTc4MzEwOTY1M3ww&ixlib=rb-4.1.0&q=85',
    entertainment: 'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwzfHxwcmVtaXVtJTIwbGl2ZSUyMGV2ZW50JTIwY3Jvd2QlMjBjb25jZXJ0JTIwbHV4dXJ5fGVufDB8fHx8MTc4MzEwOTY1M3ww&ixlib=rb-4.1.0&q=85',
    creative: 'https://images.unsplash.com/photo-1563841930606-67e2bce48b78?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHxwcmVtaXVtJTIwbGl2ZSUyMGV2ZW50JTIwY3Jvd2QlMjBjb25jZXJ0JTIwbHV4dXJ5fGVufDB8fHx8MTc4MzEwOTY1M3ww&ixlib=rb-4.1.0&q=85',
    mice: 'https://images.unsplash.com/photo-1429962714451-bb934ecdc4ec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHw0fHxwcmVtaXVtJTIwbGl2ZSUyMGV2ZW50JTIwY3Jvd2QlMjBjb25jZXJ0JTIwbHV4dXJ5fGVufDB8fHx8MTc4MzEwOTY1M3ww&ixlib=rb-4.1.0&q=85',
    diff_creative: 'https://images.unsplash.com/photo-1459749411175-04bf5292ceea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxwcmVtaXVtJTIwbGl2ZSUyMGV2ZW50JTIwY3Jvd2QlMjBjb25jZXJ0JTIwbHV4dXJ5fGVufDB8fHx8MTc4MzEwOTY1M3ww&ixlib=rb-4.1.0&q=85',
    diff_precision: 'https://images.unsplash.com/photo-1523525930602-e1239a31310b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHw0fHxjaW5lbWF0aWMlMjBjb3Jwb3JhdGUlMjBldmVudCUyMHN0YWdlJTIwbGVkJTIwd2FsbCUyMGxpZ2h0aW5nfGVufDB8fHx8MTc4MzEwOTY1M3ww&ixlib=rb-4.1.0&q=85',
    diff_technical: 'https://images.unsplash.com/photo-1596040078821-9b5f50e71ea5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxjaW5lbWF0aWMlMjBjb3Jwb3JhdGUlMjBldmVudCUyMHN0YWdlJTIwbGVkJTIwd2FsbCUyMGxpZ2h0aW5nfGVufDB8fHx8MTc4MzEwOTY1M3ww&ixlib=rb-4.1.0&q=85',
    diff_impact: 'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwzfHxwcmVtaXVtJTIwbGl2ZSUyMGV2ZW50JTIwY3Jvd2QlMjBjb25jZXJ0JTIwbHV4dXJ5fGVufDB8fHx8MTc4MzEwOTY1M3ww&ixlib=rb-4.1.0&q=85',
    gifting: 'https://images.unsplash.com/photo-1549465220-1a8b9238cd48?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85',
};

const SERVICES_DATA = [
    {
        id: 'corporate',
        icon: Sparkles,
        img: IMGS.corporate,
        title: 'Corporate Events',
        desc: 'Landmark experiences designed to celebrate people, milestones and brand vision.',
        items: ['Annual Days', 'Award Nights', 'Sales Conferences', 'Product Launches', 'Leadership Summits'],
    },
    {
        id: 'production',
        icon: Cog,
        img: IMGS.production,
        title: 'Event Production & Technical Solutions',
        desc: 'Show-stopping stage craft powered by world-class technical direction.',
        items: ['Stage Design', 'Sound & Lighting', 'LED Walls', 'Laser Shows', 'Special Effects', 'Show Direction'],
    },
    {
        id: 'entertainment',
        icon: Music,
        img: IMGS.entertainment,
        title: 'Entertainment & Artist Management',
        desc: 'Curated talent line-ups that headline your moment with cultural impact.',
        items: ['Celebrities', 'Singers', 'DJs', 'Emcees', 'Dance Troupes', 'Specialty Acts'],
    },
    {
        id: 'creative',
        icon: Wand2,
        img: IMGS.creative,
        title: 'Creative Productions',
        desc: 'Original concepts, narratives and choreography that anchor brand storytelling.',
        items: ['Concept Development', 'Theme Shows', 'Choreography', 'Opening Ceremonies', 'Cultural Productions'],
    },
    {
        id: 'mice',
        icon: Plane,
        img: IMGS.mice,
        title: 'MICE & Destination Events',
        desc: 'End-to-end management for high-touch corporate travel and offsites.',
        items: ['Conferences', 'Incentive Programs', 'Corporate Offsites', 'Hospitality', 'Logistics'],
    },
    {
        id: 'gifting',
        icon: Gift,
        img: IMGS.gifting,
        title: 'Corporate Gifting & Brand Merchandise',
        desc: 'Premium gifting experiences and customized brand merchandise designed to create meaningful connections.',
        items: ['Premium Hampers', 'Corporate Merchandise', 'Customized Gifts', 'Brand Collaterals'],
    },
];

const DIFFERENCE = [
    { id: 'creative', icon: Lightbulb, img: IMGS.diff_creative, title: 'Creative Thinking', desc: 'Original concepts designed around your brand objectives.' },
    { id: 'precision', icon: Target, img: IMGS.diff_precision, title: 'Precision Execution', desc: 'Every detail planned and delivered with excellence.' },
    { id: 'technical', icon: Cog, img: IMGS.diff_technical, title: 'Technical Excellence', desc: 'Professional production, lighting, sound and visual experiences.' },
    { id: 'impact', icon: Users, img: IMGS.diff_impact, title: 'Lasting Impact', desc: 'Experiences created to engage audiences and strengthen brands.' },
];

export const Services = () => {
    return (
        <section id="services" data-testid={SERVICES.root} className="relative py-24 sm:py-32 bg-black overflow-hidden">
            <div className="container-lux">
                {/* Section header */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 items-end">
                    <div className="lg:col-span-6">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="w-8 h-px bg-gold" />
                            <span className="eyebrow">Services</span>
                            <span className="text-white/30 text-[10px] tracking-[0.3em]">02</span>
                        </div>
                        <h2 className="font-display font-light text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                            An End-to-End
                            <br />
                            Experience <span className="italic text-gold font-extralight">Partner.</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-5 lg:col-start-8">
                        <p className="text-white/60 text-base leading-relaxed font-light">
                            From strategic concept to final curtain call — we own every layer of experience design so your brand shows up flawlessly on stage, on screen and in the room.
                        </p>
                    </div>
                </div>

                {/* Services grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {SERVICES_DATA.map((s, i) => {
                        const Icon = s.icon;
                        return (
                            <motion.div
                                key={s.id}
                                data-testid={SERVICES.card(s.id)}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: '-60px' }}
                                transition={{ duration: 0.6, delay: i * 0.08 }}
                                className="group relative bg-neutral-900/40 border border-white/10 hover:border-gold/50 hover:-translate-y-1 transition-all duration-500 flex flex-col overflow-hidden"
                            >
                                {/* Card image */}
                                <div className="relative aspect-[16/10] overflow-hidden">
                                    <img
                                        src={s.img}
                                        alt={s.title}
                                        className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-110"
                                        style={{ filter: 'brightness(0.75)' }}
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                                    <div className="absolute top-4 left-4 w-11 h-11 border border-gold/50 flex items-center justify-center text-gold bg-black/50 backdrop-blur-sm group-hover:bg-gold group-hover:text-black transition-colors">
                                        <Icon size={18} strokeWidth={1.4} />
                                    </div>
                                    <div className="absolute top-4 right-4 text-gold/70 text-[10px] tracking-[0.3em] bg-black/50 backdrop-blur-sm px-2 py-1">
                                        0{i + 1}
                                    </div>
                                </div>

                                <div className="p-8 flex-1 flex flex-col">
                                    <h3 className="font-display text-white text-xl font-medium tracking-tight mb-3">{s.title}</h3>
                                    <p className="text-white/60 text-sm leading-relaxed mb-6 font-light">{s.desc}</p>
                                    <div className="w-8 h-px bg-white/20 mb-6 group-hover:w-16 group-hover:bg-gold transition-all duration-500" />
                                    <ul className="space-y-2 mt-auto">
                                        {s.items.map((item) => (
                                            <li key={item} className="text-white/70 text-sm flex items-center gap-3 font-light">
                                                <span className="w-1 h-1 bg-gold rounded-full" />
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>

                {/* Mindstorm Difference */}
                <div data-testid={SERVICES.difference} className="mt-32">
                    <div className="text-center mb-16">
                        <div className="flex items-center justify-center gap-4 mb-6">
                            <div className="w-8 h-px bg-gold" />
                            <span className="eyebrow">Our Edge</span>
                            <div className="w-8 h-px bg-gold" />
                        </div>
                        <h3 className="font-display font-light text-white text-4xl sm:text-5xl tracking-tight">
                            The <span className="italic text-gold font-extralight">Mindstorm</span> Difference
                        </h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border border-white/10">
                        {DIFFERENCE.map((d, i) => {
                            const Icon = d.icon;
                            return (
                                <motion.div
                                    key={d.id}
                                    data-testid={SERVICES.diffCard(d.id)}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5, delay: i * 0.1 }}
                                    className={`relative p-10 group overflow-hidden transition-colors ${i < DIFFERENCE.length - 1 ? 'md:border-r border-white/10' : ''} ${i < 2 ? 'md:border-b lg:border-b-0' : ''}`}
                                >
                                    {/* Subtle background image */}
                                    <div className="absolute inset-0 overflow-hidden">
                                        <img
                                            src={d.img}
                                            alt=""
                                            aria-hidden="true"
                                            className="w-full h-full object-cover opacity-15 group-hover:opacity-25 group-hover:scale-105 transition-all duration-700"
                                            style={{ filter: 'brightness(0.6) grayscale(0.3)' }}
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/80 group-hover:from-black/40 group-hover:via-black/40 transition-colors" />
                                    </div>
                                    <div className="relative z-10">
                                        <Icon size={28} strokeWidth={1.2} className="text-gold mb-8 group-hover:scale-110 transition-transform" />
                                        <div className="text-gold/50 text-[10px] tracking-[0.3em] mb-3">0{i + 1}</div>
                                        <h4 className="font-display text-white text-lg font-medium mb-3">{d.title}</h4>
                                        <p className="text-white/60 text-sm leading-relaxed font-light">{d.desc}</p>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Services;
